// BreakdownCard — renderer for viz: breakdown, consolidated behind toggles
// (the Amberdata Contracts/Trades/Notional pattern). A report's charts[] are
// measure/channel VARIANTS of the same dimensions, distinguished by the chart
// key:
//   measure  — `premium` (bare or `premium_`), `notional_<dim>`, `trades_<dim>`
//   channel  — `<dim>_block` / `<dim>_screen` (structures), else `all`
// The card groups charts by dimension, offers a Premium/Notional/Trades toggle
// (and, when present, an All/Block/Screen channel toggle), and renders ONE donut
// per dimension for the selected variant — falling back to premium/all so it
// never blanks. Generic: works for any breakdown; the only report-specific bit
// is the opt-in structures treatment (dropSlice/topN/categorical).

import { useState } from 'react';
import { Box, Stack } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import { fmtUsd, METRIC_ORDER } from '../../../../providers/options-flow/options-flow.helpers';
import {
  BreakdownChart,
  BreakdownReport,
  BreakdownSlice,
} from '../../../../providers/options-flow/types';
import { FlowCard } from '../flow-card';
import { FlowEmptyState } from '../flow-empty-state';

// Per-dimension breakdown treatment. Card-wide props apply the same opts to
// every donut; `dimOpts` overrides them for a SINGLE dimension — the seam that
// lets a composed panel (IM-1942) mix a categorical `structures` donut with
// tone-palette siblings under one card.
export interface DimOpts {
  dropSlice?: string;
  topN?: number;
  categorical?: boolean;
  // Message shown IN-PLACE when this dimension's slot has no data (empty slices)
  // — e.g. the Structures slot on a venue that doesn't tag structures (IM-1945).
  // Absent => the generic "No premium" empty state.
  emptyMessage?: string;
}

interface BreakdownCardProps extends DimOpts {
  report: BreakdownReport;
  // Per-DIMENSION opts, keyed by dimension id — overrides the card-wide props
  // above for that donut only. Absent => every donut uses the card-wide opts.
  dimOpts?: Record<string, DimOpts>;
  // Caption override. Absent => the structured-share / generic default below.
  caption?: string;
}

// ---- Colour maps ----------------------------------------------------------

const lighten = (token: string): string => `color-mix(in srgb, ${token} 55%, #ffffff)`;

// tone -> colour (composition/session). Positioning is coloured on the
// BULL/BEAR axis: call_buy/put_sell = blue family (bullish), put_buy/call_sell =
// red family (bearish). Unknown -> neutral.
export const toneColor = (tone: string): string => {
  switch (tone) {
    case 'buy':
      return tokens.flow.buy;
    case 'sell':
      return tokens.flow.sell;
    case 'call':
      return tokens.charts.lightBlue;
    case 'put':
      return tokens.charts.purple;
    case 'block':
      return tokens.flow.accent;
    case 'screen':
      return tokens.text.tertiary;
    case 'asia':
      return tokens.charts.turquoise;
    case 'eu':
      return tokens.charts.oliveGreen;
    case 'us':
      return tokens.charts.orange;
    case 'call_buy':
      return tokens.flow.buy;
    case 'put_sell':
      return lighten(tokens.flow.buy);
    case 'put_buy':
      return tokens.flow.sell;
    case 'call_sell':
      return lighten(tokens.flow.sell);
    default:
      return tokens.text.muted;
  }
};

// Categorical palette for structure labels: curated hues for the known set, a
// deterministic label-hash hue otherwise (stable + distinct, not neutral).
const STRUCTURE_HUES: Record<string, number> = {
  call_spread: 145,
  put_spread: 275,
  straddle: 190,
  strangle: 30,
  calendar: 320,
  diagonal: 55,
  ratio: 170,
  risk_reversal: 40,
  butterfly: 300,
  condor: 95,
  custom: 230,
};

const hashHue = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

export const structureColor = (key: string): string => {
  if (key === 'other') return 'hsl(0, 0%, 55%)';
  const hue = STRUCTURE_HUES[key] ?? hashHue(key);
  return `hsl(${hue}, 62%, 58%)`;
};

// ---- Grouping (pure) ------------------------------------------------------

// Measure-toggle order follows the canonical METRIC_ORDER (Notional · Premium ·
// Trades). The breakdown's measure ids happen to match the metric ids 1:1.
export const MEASURE_ORDER = METRIC_ORDER;
const CHANNEL_ORDER = ['all', 'block', 'screen'] as const;

export interface ParsedChart {
  dimension: string;
  measure: string;
  channel: string;
  chart: BreakdownChart;
}

// Split a chart key into { dimension, measure, channel }. Measure is a
// prefix/suffix (`notional_` / `trades_` / `premium_`, bare -> premium); channel
// is a `_block`/`_screen` (or `block_`/`screen_`) marker on the remainder.
export const parseChartKey = (key: string): Omit<ParsedChart, 'chart'> => {
  let rest = key;
  let measure = 'premium';
  for (const m of ['notional', 'trades', 'premium']) {
    if (rest.startsWith(`${m}_`)) {
      measure = m;
      rest = rest.slice(m.length + 1);
      break;
    }
    if (rest.endsWith(`_${m}`)) {
      measure = m;
      rest = rest.slice(0, rest.length - (m.length + 1));
      break;
    }
  }
  let channel = 'all';
  for (const c of ['block', 'screen']) {
    if (rest.endsWith(`_${c}`)) {
      channel = c;
      rest = rest.slice(0, rest.length - (c.length + 1));
      break;
    }
    if (rest.startsWith(`${c}_`)) {
      channel = c;
      rest = rest.slice(c.length + 1);
      break;
    }
  }
  return { dimension: rest, measure, channel };
};

const orderedUnique = <T extends string>(values: string[], order: readonly T[]): string[] => {
  const present = new Set(values);
  const head = order.filter((o) => present.has(o));
  const tail = [...present].filter((v) => !order.includes(v as T));
  return [...head, ...tail];
};

export interface GroupedBreakdown {
  parsed: ParsedChart[];
  dimensions: string[];
  measures: string[];
  channels: string[];
}

export const groupCharts = (charts: BreakdownChart[]): GroupedBreakdown => {
  const parsed = charts.map((chart) => ({ ...parseChartKey(chart.key), chart }));
  const dimensions: string[] = [];
  for (const p of parsed) if (!dimensions.includes(p.dimension)) dimensions.push(p.dimension);
  const measures = orderedUnique(
    parsed.map((p) => p.measure),
    MEASURE_ORDER,
  );
  const channels = orderedUnique(
    parsed.map((p) => p.channel),
    CHANNEL_ORDER,
  );
  return { parsed, dimensions, measures, channels };
};

// The chart to show for a dimension at the selected measure/channel, falling
// back (premium / all) so a dimension missing the exact variant never blanks.
export const selectVariant = (
  parsed: ParsedChart[],
  dimension: string,
  measure: string,
  channel: string,
): BreakdownChart | null => {
  const forDim = parsed.filter((p) => p.dimension === dimension);
  const find = (m: string, c: string) =>
    forDim.find((p) => p.measure === m && p.channel === c)?.chart ?? null;
  return (
    find(measure, channel) ??
    find('premium', channel) ??
    find(measure, 'all') ??
    find('premium', 'all') ??
    forDim[0]?.chart ??
    null
  );
};

// Drop a slice (by tone or label) then fold the long tail past topN into "Other".
export const prepareSlices = (
  slices: BreakdownSlice[] | undefined,
  opts: { dropSlice?: string; topN?: number },
): BreakdownSlice[] => {
  let out = slices ?? [];
  if (opts.dropSlice) {
    const drop = opts.dropSlice.toLowerCase();
    out = out.filter((s) => s.tone?.toLowerCase() !== drop && s.label?.toLowerCase() !== drop);
  }
  if (opts.topN && out.length > opts.topN) {
    const sorted = [...out].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const head = sorted.slice(0, opts.topN);
    const otherVal = sorted.slice(opts.topN).reduce((sum, s) => sum + Math.abs(s.value), 0);
    out = otherVal > 0 ? [...head, { label: 'Other', value: otherVal, tone: 'other' }] : head;
  }
  return out;
};

// ---- UI -------------------------------------------------------------------

const Toggle = ({
  options,
  active,
  onSelect,
}: {
  options: { value: string; label: string }[];
  active: string;
  onSelect: (v: string) => void;
}) => (
  <Stack direction="row" spacing={0.75} role="tablist" sx={{ flexWrap: 'wrap' }}>
    {options.map((o) => {
      const isActive = o.value === active;
      return (
        <Box
          key={o.value}
          role="tab"
          aria-selected={isActive}
          onClick={() => onSelect(o.value)}
          sx={{
            cursor: 'pointer',
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.3px',
            px: '9px',
            py: '3px',
            borderRadius: '5px',
            color: isActive ? tokens.flow.accent : tokens.text.muted,
            backgroundColor: isActive ? tokens.bg.widget : 'transparent',
            border: `1px solid ${isActive ? tokens.flow.accent : tokens.border.default}`,
            '&:hover': { backgroundColor: tokens.bg.widget },
          }}
        >
          {o.label}
        </Box>
      );
    })}
  </Stack>
);

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// A donut chart via the stacked-stroke technique: one <circle> per segment,
// stroke-dasharray sized to its share, stroke-dashoffset stacking them, rotated
// so the first starts at 12 o'clock. Colours are CSS strings (token vars /
// hsl / color-mix) so they go in `style.stroke`, not the SVG attribute.
const Donut = ({
  segments,
  label,
  size = 116,
  thickness = 20,
}: {
  segments: { key: string; color: string; value: number }[];
  label: string;
  size?: number;
  thickness?: number;
}) => {
  const total = segments.reduce((sum, s) => sum + Math.abs(s.value), 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  // Precompute each arc's length + cumulative start offset via a reduce, so the
  // render map below is pure (no offset mutated inside it).
  const arcs = segments.reduce<{ key: string; color: string; len: number; offset: number }[]>(
    (acc, seg) => {
      const len = (Math.abs(seg.value) / total) * c;
      const prev = acc[acc.length - 1];
      const offset = prev ? prev.offset + prev.len : 0;
      acc.push({ key: seg.key, color: seg.color, len, offset });
      return acc;
    },
    [],
  );

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        strokeWidth={thickness}
        style={{ stroke: tokens.bg.surfaceDark }}
      />
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {arcs.map((arc) => (
          <circle
            key={arc.key}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={thickness}
            strokeDasharray={`${arc.len} ${c - arc.len}`}
            strokeDashoffset={-arc.offset}
            style={{ stroke: arc.color }}
          />
        ))}
      </g>
    </svg>
  );
};

const BreakdownTile = ({
  chart,
  colorOf,
  orientation = 'column',
  emptyMessage,
}: {
  chart: BreakdownChart;
  colorOf: (slice: BreakdownSlice) => string;
  // 'column' (composition's side-by-side donuts): donut over legend.
  // 'row' (single-donut card): donut + legend as a tight horizontal pair, so a
  // half-width card fills rather than floating a centred donut with dead space.
  orientation?: 'row' | 'column';
  // Overrides the "No premium" empty state when this slot has no data — e.g. the
  // venue-named "Not tagged on …" message for the Structures slot (IM-1945).
  emptyMessage?: string;
}) => {
  const slices = chart.slices ?? [];
  const total = slices.reduce((sum, s) => sum + Math.abs(s.value), 0);
  const row = orientation === 'row';

  return (
    <Box>
      <Box sx={{ fontSize: 10.5, color: tokens.text.secondary, fontWeight: 600, mb: 0.75 }}>
        {chart.label}
      </Box>

      {total <= 0 ? (
        // A custom message (the untagged-structures copy) can run two lines, so
        // give it room rather than clamping to the terse 40px "No premium" box.
        <FlowEmptyState height={emptyMessage ? 64 : 40} message={emptyMessage ?? 'No premium'} />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: row ? 'row' : 'column',
            alignItems: 'center',
            gap: row ? '18px' : 0,
          }}
        >
          <Donut
            label={chart.label}
            segments={slices.map((s) => ({
              key: `${chart.key}-${s.label}`,
              color: colorOf(s),
              value: s.value,
            }))}
          />
          <Box
            sx={{
              ...(row ? { flex: 1, minWidth: 0 } : { mt: 1, width: '100%' }),
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {slices.map((s) => (
              <Box
                key={`${chart.key}-${s.label}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 9.5,
                  color: tokens.text.muted,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '2px',
                    backgroundColor: colorOf(s),
                    flexShrink: 0,
                  }}
                />
                <Box component="span" sx={{ color: tokens.text.secondary }}>
                  {s.label}
                </Box>
                <Box
                  component="span"
                  sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                >
                  {fmtUsd(s.value)}
                </Box>
                <Box component="span">{Math.round((Math.abs(s.value) / total) * 100)}%</Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const BreakdownCard = ({
  report,
  dropSlice,
  topN,
  categorical,
  dimOpts,
  caption: captionOverride,
}: BreakdownCardProps) => {
  const charts = report.charts ?? [];
  const { parsed, dimensions, measures, channels } = groupCharts(charts);

  const [measure, setMeasure] = useState('premium');
  const [channel, setChannel] = useState('all');
  // Fall back to an available option if the persisted selection isn't present.
  const activeMeasure = measures.includes(measure) ? measure : (measures[0] ?? 'premium');
  const activeChannel = channels.includes(channel) ? channel : (channels[0] ?? 'all');

  // Resolve opts for a donut: its per-dimension override, else the card-wide
  // props. The palette + slice prep below both read off this, so a categorical
  // structures donut can sit beside tone-palette siblings under one card.
  const optsFor = (dim: string): DimOpts => dimOpts?.[dim] ?? { dropSlice, topN, categorical };
  const colorFor = (opts: DimOpts) =>
    opts.categorical
      ? (s: BreakdownSlice) => structureColor(s.tone || s.label)
      : (s: BreakdownSlice) => toneColor(s.tone);

  const structuredPct = report.meta.structured_share_pct;
  const caption =
    captionOverride ??
    (structuredPct != null
      ? `${structuredPct}% structured · ${Number((100 - structuredPct).toFixed(1))}% single-leg`
      : 'Premium share by call/put, taker side, and channel');

  const donuts = dimensions
    .map((dim) => ({ dim, chart: selectVariant(parsed, dim, activeMeasure, activeChannel) }))
    .filter((d): d is { dim: string; chart: BreakdownChart } => d.chart !== null);

  // Single-donut cards (session/structures) sit half-width in the page grid, so
  // pair their donut + legend horizontally; multi-donut composition stacks each.
  const orientation = dimensions.length <= 1 ? 'row' : 'column';

  return (
    <FlowCard title={report.title} caption={caption}>
      {charts.length === 0 ? (
        <FlowEmptyState />
      ) : (
        <>
          {(measures.length > 1 || channels.length > 1) && (
            <Stack direction="row" spacing={2} sx={{ mb: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
              {measures.length > 1 && (
                <Toggle
                  options={measures.map((m) => ({ value: m, label: cap(m) }))}
                  active={activeMeasure}
                  onSelect={setMeasure}
                />
              )}
              {channels.length > 1 && (
                <Toggle
                  options={channels.map((c) => ({ value: c, label: cap(c) }))}
                  active={activeChannel}
                  onSelect={setChannel}
                />
              )}
            </Stack>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '18px',
            }}
          >
            {donuts.map(({ dim, chart }) => {
              const opts = optsFor(dim);
              return (
                <BreakdownTile
                  key={dim}
                  chart={{
                    ...chart,
                    slices: prepareSlices(chart.slices, {
                      dropSlice: opts.dropSlice,
                      topN: opts.topN,
                    }),
                  }}
                  colorOf={colorFor(opts)}
                  orientation={orientation}
                  emptyMessage={opts.emptyMessage}
                />
              );
            })}
          </Box>
        </>
      )}
    </FlowCard>
  );
};
